import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Navigation from '../../../components/feature/Navigation';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';
import Button from '../../../components/base/Button';
import Card from '../../../components/base/Card';
import { countries, documentTypes, paymentTypes, courierCompanies } from '../../../mocks/countries';

export default function NewJob() {
  const navigate = useNavigate();
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [formData, setFormData] = useState({
    // Client Information
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientAddress: '',
    
    // Document Information
    documentType: '',
    destinationCountry: '',
    
    // Shipping Information
    shippingCompany: '',
    trackingNumberTo: '',
    trackingNumberFrom: '',
    shippingCost: '',
    
    // Courier Information
    courierName: '',
    courierPhone: '',
    courierAddress: '',
    courierFee: '',
    
    // Processing Dates
    sentToSoSDate: '',
    receivedFromSoSDate: '',
    sentToUSDoSDate: '',
    receivedFromUSDoSDate: '',
    
    // Apostille Information
    apostilleSerialNumber: '',
    apostillePhoto: '',
    
    // Payment Information
    paymentDate: '',
    paymentType: '',
    totalAmount: '',
    paymentReceived: false,
    
    // Status
    status: 'pending'
  });

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCountrySelect = (country: any) => {
    const countryLabel = `${country.name} ${country.hagueConvention ? '(Hague)' : '(Non-Hague)'}`;
    setFormData(prev => ({
      ...prev,
      destinationCountry: country.name
    }));
    setCountrySearch(countryLabel);
    setShowCountryDropdown(false);
  };

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.clientName || !formData.documentType || !formData.destinationCountry) {
      alert('Please fill in all required fields');
      return;
    }

    // Generate unique ID
    const jobId = Date.now().toString();
    
    const newJob = {
      ...formData,
      id: jobId,
      createdDate: new Date().toISOString(),
      totalAmount: parseFloat(formData.totalAmount) || 0,
      shippingCost: parseFloat(formData.shippingCost) || 0,
      courierFee: parseFloat(formData.courierFee) || 0
    };

    // Save to localStorage
    const existingJobs = JSON.parse(localStorage.getItem('apostille_jobs') || '[]');
    existingJobs.push(newJob);
    localStorage.setItem('apostille_jobs', JSON.stringify(existingJobs));

    navigate('/jobs');
  };

  const countryOptions = countries.map(country => ({
    value: country.name,
    label: `${country.name} ${country.hagueConvention ? '(Hague)' : '(Non-Hague)'}`
  }));

  const documentOptions = documentTypes.map(type => ({
    value: type,
    label: type
  }));

  const paymentOptions = paymentTypes.map(type => ({
    value: type,
    label: type
  }));

  const shippingCompanyOptions = courierCompanies.map(company => ({
    value: company,
    label: company
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        title="New Job" 
        showBack 
        onBack={() => navigate('/jobs')}
      />
      
      <div className="pt-16 pb-20 px-4">
        <div className="space-y-6">
          {/* Client Information */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
            <div className="space-y-4">
              <Input
                label="Client Name"
                value={formData.clientName}
                onChange={(value) => handleInputChange('clientName', value)}
                required
              />
              <Input
                label="Email"
                type="email"
                value={formData.clientEmail}
                onChange={(value) => handleInputChange('clientEmail', value)}
              />
              <Input
                label="Phone"
                type="tel"
                value={formData.clientPhone}
                onChange={(value) => handleInputChange('clientPhone', value)}
              />
              <Input
                label="Address"
                value={formData.clientAddress}
                onChange={(value) => handleInputChange('clientAddress', value)}
              />
            </div>
          </Card>

          {/* Document Information */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Information</h3>
            <div className="space-y-4">
              <Select
                label="Document Type"
                value={formData.documentType}
                onChange={(value) => handleInputChange('documentType', value)}
                options={documentOptions}
                required
              />
              
              {/* Searchable Country Selector */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destination Country <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={countrySearch}
                    onChange={(e) => {
                      setCountrySearch(e.target.value);
                      setShowCountryDropdown(true);
                    }}
                    onFocus={() => setShowCountryDropdown(true)}
                    placeholder="Search for a country..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <i className="ri-search-line text-gray-400"></i>
                  </div>
                </div>
                
                {showCountryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleCountrySelect(country)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none text-sm border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{country.name}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              country.hagueConvention 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {country.hagueConvention ? 'Hague' : 'Non-Hague'}
                            </span>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No countries found
                      </div>
                    )}
                  </div>
                )}
                
                {/* Click outside to close dropdown */}
                {showCountryDropdown && (
                  <div 
                    className="fixed inset-0 z-5"
                    onClick={() => setShowCountryDropdown(false)}
                  />
                )}
              </div>
            </div>
          </Card>

          {/* Shipping Information */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Information</h3>
            <div className="space-y-4">
              <Select
                label="Shipping Company"
                value={formData.shippingCompany}
                onChange={(value) => handleInputChange('shippingCompany', value)}
                options={shippingCompanyOptions}
              />
              <Input
                label="Tracking Number (TO)"
                value={formData.trackingNumberTo}
                onChange={(value) => handleInputChange('trackingNumberTo', value)}
                placeholder="Tracking number for outbound shipment"
              />
              <Input
                label="Tracking Number (FROM)"
                value={formData.trackingNumberFrom}
                onChange={(value) => handleInputChange('trackingNumberFrom', value)}
                placeholder="Tracking number for return shipment"
              />
              <Input
                label="Shipping Cost"
                type="number"
                value={formData.shippingCost}
                onChange={(value) => handleInputChange('shippingCost', value)}
                placeholder="0.00"
              />
            </div>
          </Card>

          {/* Courier Information */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Courier Information</h3>
            <div className="space-y-4">
              <Input
                label="Courier Name"
                value={formData.courierName}
                onChange={(value) => handleInputChange('courierName', value)}
              />
              <Input
                label="Courier Phone"
                type="tel"
                value={formData.courierPhone}
                onChange={(value) => handleInputChange('courierPhone', value)}
              />
              <Input
                label="Courier Address"
                value={formData.courierAddress}
                onChange={(value) => handleInputChange('courierAddress', value)}
              />
              <Input
                label="Courier Fee"
                type="number"
                value={formData.courierFee}
                onChange={(value) => handleInputChange('courierFee', value)}
                placeholder="0.00"
              />
            </div>
          </Card>

          {/* Processing Dates */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Dates</h3>
            <div className="space-y-4">
              <Input
                label="Sent to State SoS"
                type="date"
                value={formData.sentToSoSDate}
                onChange={(value) => handleInputChange('sentToSoSDate', value)}
              />
              <Input
                label="Received from State SoS"
                type="date"
                value={formData.receivedFromSoSDate}
                onChange={(value) => handleInputChange('receivedFromSoSDate', value)}
              />
              <Input
                label="Sent to US DoS"
                type="date"
                value={formData.sentToUSDoSDate}
                onChange={(value) => handleInputChange('sentToUSDoSDate', value)}
              />
              <Input
                label="Received from US DoS"
                type="date"
                value={formData.receivedFromUSDoSDate}
                onChange={(value) => handleInputChange('receivedFromUSDoSDate', value)}
              />
            </div>
          </Card>

          {/* Apostille Information */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Apostille Information</h3>
            <div className="space-y-4">
              <Input
                label="Apostille Serial Number"
                value={formData.apostilleSerialNumber}
                onChange={(value) => handleInputChange('apostilleSerialNumber', value)}
                placeholder="Enter serial number from completed apostille"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Apostille Photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        handleInputChange('apostillePhoto', event.target?.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                {formData.apostillePhoto && (
                  <div className="mt-2">
                    <img 
                      src={formData.apostillePhoto} 
                      alt="Apostille preview" 
                      className="w-32 h-32 object-cover rounded-lg border"
                    />
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Payment Information */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
            <div className="space-y-4">
              <Input
                label="Total Amount"
                type="number"
                value={formData.totalAmount}
                onChange={(value) => handleInputChange('totalAmount', value)}
                placeholder="0.00"
              />
              <Select
                label="Payment Type"
                value={formData.paymentType}
                onChange={(value) => handleInputChange('paymentType', value)}
                options={paymentOptions}
              />
              <Input
                label="Payment Date"
                type="date"
                value={formData.paymentDate}
                onChange={(value) => handleInputChange('paymentDate', value)}
              />
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="paymentReceived"
                  checked={formData.paymentReceived}
                  onChange={(e) => handleInputChange('paymentReceived', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="paymentReceived" className="text-sm text-gray-700">
                  Payment Received
                </label>
              </div>
            </div>
          </Card>

          {/* Submit Button */}
          <div className="pt-4">
            <Button
              onClick={handleSubmit}
              className="w-full"
              size="lg"
            >
              Create Job
            </Button>
          </div>
        </div>
      </div>

      <Navigation />
    </div>
  );
}
